import PageHeader from '../../components/kit/PageHeader';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { useSearchParams } from 'react-router-dom';
import DuplicateReview from './DuplicateReview';
import { toast } from 'sonner';
import { Search, Plus, Lock, Undo2, ArrowLeftRight, ArrowDownLeft, ArrowUpRight, CreditCard, Landmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { fmtMoney } from '../../components/kit/format';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Money from '../../components/kit/Money';
import Pill from '../../components/kit/Pill';
import SpendTotals from '../../components/kit/SpendTotals';
import { plannedForMonths, summarizePlannedIn } from '../planned/plannedEntries';
import { periodMonths } from './period';
import LeafHeader from '../../components/kit/LeafHeader';
import FilterChips from '../../components/kit/FilterChips';
import { getAllDebtPayments } from '../../../services/debtService';
import { signedAmount } from '../../../finance';
import { useData, useCoreReady } from '../../store/data';
import { useEntrySheet } from '../entry/useEntrySheet';
import { buildEntries, filterEntries, summarize, type EntryKind, type LedgerEntry } from './entries';
import { confirmAsk } from '../../components/confirm';
import { latestDebtPayments, reverseDialog, runReverse } from './reverse';
import { currentPeriod, stepPeriod, switchMode, periodFilter, type Period, type PeriodMode } from './period';

const FILTERS = [
  { id: 'all', label: 'Tất cả' }, { id: 'expense', label: 'Chi' }, { id: 'income', label: 'Thu' }, { id: 'other', label: 'Chuyển & trả nợ' }
] as const;
type FilterId = typeof FILTERS[number]['id'];

/** Biểu tượng tròn theo loại: chi đỏ, thu và hoàn tiền xanh, trả thẻ/nợ xanh dương, chuyển khoản trung tính. */
const TONE: Record<EntryKind, string> = {
  income: 'bg-good-soft text-good', refund: 'bg-good-soft text-good', expense: 'bg-bad-soft text-bad',
  cardPayment: 'bg-accent text-accent-foreground', debtPayment: 'bg-accent text-accent-foreground', transfer: 'bg-secondary text-muted-foreground'
};
const ICON: Record<EntryKind, typeof ArrowUpRight> = {
  income: ArrowDownLeft, expense: ArrowUpRight, refund: ArrowDownLeft, transfer: ArrowLeftRight, cardPayment: CreditCard, debtPayment: Landmark
};
const PAGE = 80;
const KIND_LABEL: Record<EntryKind, string> = { income: 'Thu', refund: 'Hoàn tiền', expense: 'Chi', transfer: 'Chuyển khoản', cardPayment: 'Trả thẻ', debtPayment: 'Trả nợ' };

const WD = ['Chủ nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
const WD_SHORT = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
const dayLabel = (d: Date) => `${WD[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;

/**
 * Thanh tổng cố định: khi khung tổng hợp trượt khỏi màn hình, kỳ đang xem cùng Thu · Chi · Còn lại dính ở đầu trang.
 * Bọc 0 chiều cao nên không làm lệch bố cục; trên điện thoại nằm dưới thanh tiêu đề cố định của khung ứng dụng.
 */
export function StickyTotals({ label, income, expense, net, show }: { label: string; income: number; expense: number; net: number; show: boolean }) {
  return (
    <div className="sticky top-12 z-20 !mt-0 h-0 md:top-0" aria-hidden={!show}>
      <div className={cn('absolute inset-x-0 top-0 flex flex-wrap items-baseline gap-x-5 gap-y-1 rounded-b-lg border-x border-b border-border bg-card px-4 py-2 shadow-sm transition-[opacity,transform] duration-150 motion-reduce:transition-none', show ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0')}>
        <span className="font-slab text-sm font-bold text-brand">{label}</span>
        <span className="num text-sm"><span className="text-muted-foreground">Thu </span><b className="font-semibold text-good">{fmtMoney(income, { compact: true })}</b></span>
        <span className="num text-sm"><span className="text-muted-foreground">Chi </span><b className="font-semibold">{fmtMoney(expense, { compact: true })}</b></span>
        <span className="num text-sm"><span className="text-muted-foreground">Còn lại </span><b className="font-semibold">{fmtMoney(net, { compact: true })}</b></span>
      </div>
    </div>
  );
}

export default function Transactions() {
  const ready = useCoreReady();
  const { incomes, expenses, transfers, creditPayments, sources } = useData(s => s.data);
  const [sp, setSp] = useSearchParams();
  const dupMode = sp.get('trung') === '1';
  const openNew = useEntrySheet(s => s.openNew);
  const openEdit = useEntrySheet(s => s.openEdit);
  const now = new Date();
  const [period, setPeriod] = useState<Period>(() => currentPeriod(now));
  const { year, month, quarter } = periodFilter(period);
  const [kind, setKind] = useState<FilterId>('all');
  const [query, setQuery] = useState('');
  const summaryRef = useRef<HTMLElement | null>(null);
  const [summaryGone, setSummaryGone] = useState(false);
  const [walletF, setWalletF] = useState('all');
  const [catF, setCatF] = useState('all');
  const [limit, setLimit] = useState(PAGE);
  const [debtPayments, setDebtPayments] = useState<any[]>([]);
  const [debtLoaded, setDebtLoaded] = useState(false);

  // Khoản trả nợ nằm ở subcollection: tải một lần khi mở trang (không realtime)
  const loadDebtPayments = () => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    getAllDebtPayments(uid).then((r: any) => { if (r?.success) { setDebtPayments(r.data ?? []); setDebtLoaded(true); } }).catch(() => {});
  };
  useEffect(loadDebtPayments, []);
  useEffect(() => setLimit(PAGE), [year, month, quarter, kind, query, walletF, catF]);

  const all = useMemo(
    () => buildEntries({ incomes, expenses, transfers, creditPayments, debtPayments, sources }),
    [incomes, expenses, transfers, creditPayments, debtPayments, sources]
  );
  const rows = useMemo(() => filterEntries(all, { kind, query, year, month, quarter, walletId: walletF === 'all' ? undefined : walletF, category: catF === 'all' ? undefined : catF }), [all, kind, query, year, month, quarter, walletF, catF]);
  const sum = useMemo(() => summarize(rows), [rows]);
  const data = useData(s => s.data);
  // Giao dịch sinh từ Dự kiến: chi/thu có plannedId, hoặc khoản dự kiến đã xác nhận trỏ tới (bản ghi cũ; trả thẻ/nợ chỉ có chiều này)
  const fromPlan = useMemo(() => {
    const ids = new Set<string>();
    for (const c of ['plannedExpenses', 'plannedIncomes', 'plannedPayments'] as const)
      for (const p of (data as any)[c] ?? []) {
        if (p.status !== 'confirmed') continue;
        for (const k of ['actualExpenseId', 'actualIncomeId', 'actualPaymentId']) if (p[k]) ids.add(p[k]);
      }
    return ids;
  }, [data]);
  const cardIds = useMemo(() => new Set<string>(sources.filter((w: any) => w.type === 'credit').map((w: any) => w.id as string)), [sources]);
  const plan = useMemo(() => {
    const entries = plannedForMonths({ plannedExpenses: data.plannedExpenses, plannedIncomes: data.plannedIncomes, plannedPayments: data.plannedPayments, recurringTemplates: data.recurringTemplates }, periodMonths(period));
    return summarizePlannedIn(entries, periodMonths(period), cardIds);
  }, [data.plannedExpenses, data.plannedIncomes, data.plannedPayments, data.recurringTemplates, period, cardIds]);
  // Đã chi thực tế = chi tiêu + trả thẻ & nợ; "ngoài dự kiến" = khoản không gắn với khoản dự kiến nào
  const periodRows = useMemo(() => filterEntries(all, { kind: 'all', query: '', year, month, quarter }), [all, year, month, quarter]);
  const catOptions = useMemo(() => [...new Set(periodRows.filter(e => e.kind === 'income' || e.kind === 'expense' || e.kind === 'refund').map(e => e.title))].sort((a, b) => a.localeCompare(b, 'vi')), [periodRows]);
  const walletOptions = useMemo(() => sources.filter((w: any) => w.status !== 'inactive').map((w: any) => ({ id: w.id as string, name: w.name as string })).sort((a, b) => a.name.localeCompare(b.name, 'vi')), [sources]);
  // Lãi vay tự sinh (relatedPaymentId) đã nằm trong tổng số tiền của lần trả nợ → loại ra để không tính hai lần
  const isLoanInterest = (e: LedgerEntry) => (e.kind === 'expense') && !!(e.raw as any)?.relatedPaymentId;
  // Tiền ra khỏi ví: khoản chi quẹt thẻ không cộng ở đây (sẽ tính khi trả thẻ); hiện riêng ở ghi chú
  const onCard = (e: LedgerEntry) => (e.kind === 'expense' || e.kind === 'refund') && !!e.walletId && cardIds.has(e.walletId);
  const cardSwiped = periodRows.filter(onCard).reduce((t, e) => t + signedAmount(e.raw), 0);
  const spent = periodRows.filter(e => !isLoanInterest(e) && !onCard(e)).reduce((t, e) => {
    if (e.kind === 'expense' || e.kind === 'refund') return t + signedAmount(e.raw);
    if (e.kind === 'cardPayment' || e.kind === 'debtPayment') return t - e.amount;
    return t;
  }, 0);
  const outsidePlan = useMemo(() => {
    const linked = new Set<string>();
    for (const c of ['plannedExpenses', 'plannedPayments'] as const) for (const d of (data[c] ?? []) as any[]) { const id = d.actualExpenseId ?? d.actualPaymentId; if (id) linked.add(id); }
    return periodRows.reduce((s, e) => {
      if (linked.has(e.id) || isLoanInterest(e) || onCard(e)) return s;
      if (e.kind === 'expense' || e.kind === 'refund') return s + signedAmount(e.raw);
      if (e.kind === 'cardPayment' || e.kind === 'debtPayment') return s - e.amount;
      return s;
    }, 0);
  }, [periodRows, data.plannedExpenses, data.plannedPayments]);
  const shown = rows.slice(0, limit);

  const groups = useMemo(() => {
    const m = new Map<string, LedgerEntry[]>();
    for (const e of shown) { const k = e.date.toDateString(); m.set(k, [...(m.get(k) ?? []), e]); }
    return [...m.entries()];
  }, [shown]);

  const step = (delta: number) => setPeriod(p => stepPeriod(p, delta));
  const periodLabel = period.mode === 'month' ? `Tháng ${period.month}/${period.year}` : period.mode === 'quarter' ? `Quý ${period.quarter}/${period.year}` : `Năm ${period.year}`;
  // Khung tổng hợp trượt khỏi màn hình phía trên → hiện thanh tổng cố định
  useEffect(() => {
    const el = summaryRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([en]) => setSummaryGone(!en.isIntersecting && en.boundingClientRect.top < 0));
    io.observe(el);
    return () => io.disconnect();
  }, [ready]);

  const latestDebt = useMemo(() => latestDebtPayments(all), [all]);
  const reverse = async (e: LedgerEntry) => {
    const dlg = reverseDialog(e, latestDebt);
    if ('blocked' in dlg) { toast.info(dlg.blocked); return; }
    if (!await confirmAsk({ ...dlg, destructive: true })) return;
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    const r = await runReverse(uid, e);
    if (!r.success) { toast.error(r.error ?? 'Không hoàn tác được'); return; }
    r.warnings?.forEach(w => toast.warning(w, { duration: 7000 }));
    toast.success(r.reopened ? 'Đã hoàn tác — khoản dự kiến đã mở lại ở mục Dự kiến' : 'Đã hoàn tác — số dư đã được trả về như trước');
    if (e.kind === 'debtPayment') loadDebtPayments();
  };
  const onRow = (e: LedgerEntry) => (e.editable ? openEdit(e) : e.reversible ? reverse(e) : toast.info(e.lockedReason ?? 'Giao dịch này chưa sửa được ở đây.'));

  if (dupMode) {
    return (
      <div className="space-y-5">
        <PageHeader title="Giao dịch" question="Khoản chi nghi ghi trùng: xem cạnh nhau rồi xoá bản thừa." />
        <DuplicateReview expenses={expenses} entries={all} linkedIds={fromPlan} onOpen={openEdit} onExit={() => setSp({}, { replace: true })} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Giao dịch" question="Tiền đã đi đâu? Thu, chi, chuyển khoản, trả thẻ và trả nợ ở một nơi.">
        <Button onClick={() => openNew()} className="shrink-0"><Plus size={16} aria-hidden /> Ghi nhanh</Button>
      </PageHeader>

      <section ref={summaryRef} aria-label="Tổng hợp kỳ đang xem" className="overflow-hidden rounded-lg border border-border bg-card">
        <LeafHeader month={month} quarter={quarter} year={year} onStep={step} />
        <div className="space-y-4 p-4 sm:p-5">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
            {[
              { l: 'Thu', v: sum.income, tone: 'in' as const },
              { l: 'Chi (đã trừ hoàn tiền)', v: sum.expense, tone: undefined },
              { l: 'Còn lại', v: sum.net, tone: undefined },
              { l: 'Trả thẻ & nợ', v: sum.debtAndCards, tone: 'muted' as const }
            ].map(x => (
              <div key={x.l}>
                <dt className="text-sm text-muted-foreground">{x.l}</dt>
                <dd>{ready ? <Money value={x.v} size="figure" tone={x.tone} compact /> : <Skeleton className="mt-1 h-7 w-24" />}</dd>
              </div>
            ))}
          </dl>
          <SpendTotals planned={plan.spend} done={spent} left={plan.spendLeft} loading={!ready || !debtLoaded} note={[cardSwiped > 0 ? `Chưa gồm ${fmtMoney(cardSwiped, { compact: true })} chi quẹt thẻ — sẽ tính khi trả thẻ.` : '', outsidePlan > 0 ? `Trong số đã chi, ${Math.round(outsidePlan).toLocaleString('vi-VN')} đ nằm ngoài dự kiến.` : ''].filter(Boolean).join(' ') || undefined} />
          <div className="flex flex-wrap items-center gap-2">
            <Tabs value={period.mode} onValueChange={v => setPeriod(p => switchMode(p, v as PeriodMode))}>
              <TabsList><TabsTrigger value="month">Theo tháng</TabsTrigger><TabsTrigger value="quarter">Theo quý</TabsTrigger><TabsTrigger value="year">Cả năm</TabsTrigger></TabsList>
            </Tabs>
            <div className="relative ml-auto min-w-44 flex-1 sm:max-w-72">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Tìm danh mục, ví, ghi chú, số tiền" className="pl-9" aria-label="Tìm giao dịch" />
            </div>
          </div>
        </div>
      </section>
      <StickyTotals label={periodLabel} income={sum.income} expense={sum.expense} net={sum.net} show={summaryGone} />

      <FilterChips value={kind} onChange={setKind} label="Lọc theo loại" options={FILTERS.map(f => ({ id: f.id, label: f.label }))} />
      <div className="flex flex-wrap items-center gap-2">
        <Select value={walletF} onValueChange={setWalletF}>
          <SelectTrigger aria-label="Lọc theo ví" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tất cả ví</SelectItem>{walletOptions.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={catF} onValueChange={setCatF}>
          <SelectTrigger aria-label="Lọc theo danh mục" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tất cả danh mục</SelectItem>{catOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        {(walletF !== 'all' || catF !== 'all') && <Button variant="ghost" size="sm" onClick={() => { setWalletF('all'); setCatF('all'); }}>Bỏ lọc</Button>}
      </div>

      {!ready ? (
        <div className="space-y-2">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}</div>
      ) : rows.length === 0 ? (
        <Card className="space-y-3 p-8 text-center">
          <p className="font-slab text-lg font-semibold">Chưa có giao dịch nào{query || kind !== 'all' ? ' khớp bộ lọc' : ' trong kỳ này'}</p>
          <p className="text-muted-foreground">{query || kind !== 'all' ? 'Thử bỏ bộ lọc hoặc đổi kỳ xem.' : 'Ghi khoản chi hoặc thu đầu tiên của kỳ này.'}</p>
          <Button onClick={() => openNew()}><Plus size={16} aria-hidden /> Ghi giao dịch</Button>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card className="overflow-hidden p-0">
            {groups.map(([day, list], gi) => {
              const d = list[0].date;
              return (
                <section key={day} aria-label={dayLabel(d)} className={cn('grid grid-cols-[3.5rem_1fr] sm:grid-cols-[5rem_1fr]', gi > 0 && 'perf-top')}>
                  <div className="pt-3.5 text-center" aria-hidden>
                    <p className="font-slab num text-3xl font-bold leading-none text-brand sm:text-4xl">{String(d.getDate()).padStart(2, '0')}</p>
                    {month === null
                      ? <p className="num mt-1 text-xs text-muted-foreground">{String(d.getMonth() + 1).padStart(2, '0')}/{d.getFullYear()}</p>
                      : <p className="mt-1 text-xs text-muted-foreground"><span className="sm:hidden">{WD_SHORT[d.getDay()]}</span><span className="hidden sm:inline">{WD[d.getDay()]}</span></p>}
                    {(() => { const net = summarize(list).net; return net !== 0 ? <p className="num mt-1 text-xs font-medium text-muted-foreground" title="Thu trừ chi trong ngày">{fmtMoney(net, { compact: true, plus: true })}</p> : null; })()}
                  </div>
                  <div>
                    {list.map((e, i) => {
                      const Icon = ICON[e.kind];
                      const positive = e.kind === 'income' || e.kind === 'refund';
                      const quiet = e.kind === 'cardPayment' || e.kind === 'debtPayment' || e.kind === 'transfer';
                      const amt = e.kind === 'transfer' ? Number(e.raw.amount) || 0 : Math.abs(e.amount);
                      const pill = e.loan ? <Pill tone="warn">Tiền vay</Pill> : e.kind === 'refund' ? <Pill tone="good">Hoàn tiền</Pill> : (e.raw?.plannedId || fromPlan.has(e.id)) ? <Pill tone="action">Từ dự kiến</Pill> : null;
                      return (
                        <button key={e.key} onClick={() => onRow(e)} className={cn('flex w-full items-center gap-3 py-3 pr-3 text-left transition-colors hover:bg-secondary/60 focus-visible:bg-secondary/60 sm:pr-4', i > 0 && 'perf-top')}>
                          <span role="img" aria-label={KIND_LABEL[e.kind]} className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', TONE[e.kind])}><Icon size={16} aria-hidden /></span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                              <span className="truncate text-base font-medium">{e.primary}</span>
                              {pill}
                              {!e.editable && (e.reversible ? <Undo2 size={13} className="shrink-0 text-muted-foreground" aria-label="Bấm để hoàn tác" /> : <Lock size={12} className="shrink-0 text-muted-foreground" aria-label="Chưa sửa được ở đây" />)}
                            </span>
                            <span className="block truncate text-sm text-muted-foreground">{e.secondary}</span>
                          </span>
                          <span className="shrink-0 text-right">
                            {e.kind === 'transfer' && <span className="mr-1 text-muted-foreground" aria-hidden>⇄</span>}
                            <Money value={positive ? amt : e.kind === 'transfer' ? amt : -amt} plus={positive} tone={positive ? 'in' : quiet ? 'muted' : undefined} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </Card>
          {rows.length > limit && (
            <div className="text-center"><Button variant="outline" onClick={() => setLimit(l => l + PAGE)}>Hiện thêm {rows.length - limit} giao dịch</Button></div>
          )}
        </div>
      )}
    </div>
  );
}
